import React, { useEffect, useState } from 'react'
import { Button, Modal, notification, Row } from 'antd'
import { useDispatch } from 'react-redux'
import { unwrapResult } from '@reduxjs/toolkit'
import { EditableList } from '../../../compnents/list/EditableList'
import { TreatmentBillingCategories, BillingCategoryName } from '../../../models/billingCategory'
import { AddTreatment } from './AddTreatment'
import { getMemberInterestPageAsync, submitMemberInterestBatchAsync, getMemberInterestExceptionPageAsync } from '../memberBenefitsSlice'
import { RootDispatch } from '../../../app/store'

interface AddTreatmentCategoryProps {
  visible: boolean
  onCancel: () => void
  onOk: (data: any[]) => void
  onRefresh?: () => void
}

export const AddTreatmentCategory = ({ visible, onCancel, onOk, onRefresh }: AddTreatmentCategoryProps) => {
  const dispatch = useDispatch<RootDispatch>()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<any[]>(() =>
    TreatmentBillingCategories.map((v) => ({
      billingCategory: v,
      discountType: 0,
      price: null,
      specialNum: 0,
    }))
  )

  const loadData = () => {
    setLoading(true)
    dispatch(getMemberInterestPageAsync({ billingCategoryFlag: true }))
      .then(unwrapResult)
      .then((res: any) => {
        const records: any[] = res?.records || res || []
        const savedSet = new Set(records.map((r: any) => r.billingCategory))
        // 只展示未保存过的分类
        setData(
          TreatmentBillingCategories
            .filter((v) => !savedSet.has(v))
            .map((v) => ({
              billingCategory: v,
              discountType: 0,
              price: null,
              specialNum: 0,
            }))
        )
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    if (visible) {
      loadData()
    }
  }, [visible])

  const [exceptionVisible, setExceptionVisible] = useState(false)
  const [exceptionRecord, setExceptionRecord] = useState<any>(null)
  const [exceptionDefaultKeys, setExceptionDefaultKeys] = useState<React.Key[]>([])
  const [exceptionDefaultRows, setExceptionDefaultRows] = useState<any[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [submitLoading, setSubmitLoading] = useState(false)

  const handleOk = () => {
    const selectedData = data.filter((row: any) => selectedRowKeys.includes(row.billingCategory))
    if (selectedData.length === 0) {
      notification.warning({ message: '请至少选择一项' })
      return
    }

    const params = selectedData.map((row: any) => ({
      name: BillingCategoryName(row.billingCategory),
      billingCategory: row.billingCategory,
      price: (row.price == null || row.price === '') ? 100 : row.price,
    }))

    setSubmitLoading(true)
    dispatch(submitMemberInterestBatchAsync(params))
      .then(unwrapResult)
      .then(() => {
        notification.success({ message: '保存成功' })
        handleCancel()
      })
      .finally(() => {
        setSubmitLoading(false)
      })
  }

  const handleCancel = () => {
    setSelectedRowKeys([])
    onCancel()
  }

  const columns = [
    {
      title: '开票项目',
      dataIndex: 'billingCategory',
      align: 'center' as const,
      key: 'billingCategory',
      render: (_: any, r: any) => BillingCategoryName(r.billingCategory),
    },
    {
      title: '折扣类型',
      dataIndex: 'discountType',
      align: 'center' as const,
      key: 'discountType',
      width: '8rem',
      render: () => '折扣',
    },
    {
      title: '会员价',
      dataIndex: 'price',
      align: 'center' as const,
      key: 'price',
      width: '5rem',
      colSpan: 2,
      className: 'noRightBorder',
      editable: (r: any) => selectedRowKeys.includes(r.billingCategory),
      rules: [
        {
          required: true,
          message: '请输入会员价',
        },
        {
          pattern: new RegExp(/^(100|[1-9]?\d)$/),
          message: '折扣请输入0-100的整数',
        },
      ],
      onSave: (prev: any, next: any) => {
        const price = next.price
        const val = Number(price)
        if (val < 0 || val > 100) {
          return
        }
        setData((d) =>
          d.map((item) =>
            item.billingCategory === prev.billingCategory ? { ...item, price: price || null } : item
          )
        )
      },
      render: (_: any, r: any) => {
        if (r.price == null) return ''
        const num = parseFloat(r.price)
        return String(Math.round(num))
      },
    },
    {
      dataIndex: 'unit',
      align: 'center' as const,
      key: 'unit',
      width: '4rem',
      colSpan: 0,
      render: function UnitCell(_: any, r: any) {
        const selected = selectedRowKeys.includes(r.billingCategory)
        return <span style={{ color: selected ? undefined : '#bbb' }}>%</span>
      },
    },
    {
      title: '例外',
      dataIndex: 'specialNum',
      align: 'center' as const,
      key: 'specialNum',
      width: '6rem',
      render: (_: any, r: any) => `例外 ${r.specialNum || 0} 个`,
    },
    {
      title: '操作',
      align: 'center' as const,
      key: 'action',
      width: '6rem',
      render: function ActionCell(_: any, r: any) {
        if (!selectedRowKeys.includes(r.billingCategory)) return null
        return (
          <Button type="link" onClick={() => {
            setExceptionRecord(r)
            dispatch(getMemberInterestExceptionPageAsync({ billingCategory: r.billingCategory, catalogType: 1 }))
              .then(unwrapResult)
              .then((res: any) => {
                const records: any[] = res?.records || res || []
                setExceptionDefaultKeys(records.map((v: any) => v.tenantCatalogId))
                setExceptionDefaultRows(records.map((v: any) => ({
                  id: v.tenantCatalogId,
                  name: v.name,
                  retailPrice: v.retailPrice,
                  stockPrice: v.stockPrice,
                  spec: v.spec,
                  catalogType: v.catalogType ?? -1,
                  discountType: v.discountType ?? 0,
                  price: v.price,
                  totalNumStr: v.totalNumStr,
                })))
                setExceptionVisible(true)
              })
          }}>
            编辑例外
          </Button>
        )
      },
    },
  ]

  return (
    <>
    <Modal
      title="添加诊疗分类"
      visible={visible}
      maskClosable={false}
      onCancel={handleCancel}
      onOk={handleOk}
      confirmLoading={submitLoading}
      width={800}
      destroyOnClose
    >
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <span>选择开票项目</span>
        <span>已选择 {selectedRowKeys.length} 项</span>
      </Row>
      <EditableList
        loading={loading}
        rowKey={(t: any) => t.billingCategory}
        bordered
        dataSource={data}
        pagination={false}
        style={{ height: 500 }}
        columns={columns}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
        }}
      />
    </Modal>
    <AddTreatment
      visible={exceptionVisible}
      title={`编辑例外诊疗 - ${exceptionRecord ? BillingCategoryName(exceptionRecord.billingCategory) : ''}`}
      mode="exception"
      billingCategory={exceptionRecord?.billingCategory}
      defaultSelectedRowKeys={exceptionDefaultKeys}
      defaultSelectedRows={exceptionDefaultRows}
      onCancel={() => {
        setExceptionVisible(false)
        setExceptionDefaultKeys([])
        setExceptionDefaultRows([])
      }}
      onOk={() => {
        const categoryRow = data.find((item) => item.billingCategory === exceptionRecord?.billingCategory)
        if (categoryRow) {
          dispatch(submitMemberInterestBatchAsync([{
            name: BillingCategoryName(categoryRow.billingCategory),
            billingCategory: categoryRow.billingCategory,
            price: categoryRow.price,
          }]))
            .then(unwrapResult)
            .then(() => {
              loadData()
              onRefresh?.()
            })
        }
        setExceptionVisible(false)
        setExceptionDefaultKeys([])
        setExceptionDefaultRows([])
      }}
    />
    </>
  )
}
